// Enumerations for Husky IVO configurations
enum HuskyIvoModel {
  Auto = 0,
  HE150D = 1,
  HE350T = 2,
  HE350R = 3,
  HE700R = 4,
  HE1000R = 5,
  HE1800R = 6
}

enum HuskyFormFactor {
  Auto = 0,
  Rack1U = 1,
  Rack2U = 2,
  Desktop = 3,
  Tower = 4
}

enum HuskyRaidType {
  Auto = 0,
  JBOD = 1,
  RAID5 = 4,
  RAID6 = 5,
  RAID10 = 6
}

// Centralized Husky IVO Configuration System Matrix
export type ConfigurationMatrix = {
  [key in HuskyIvoModel]: {
    [key in HuskyFormFactor]?: HuskyRaidType[]
  }
}

// Centralized configuration matrix defining all valid combinations
export const HUSKY_CONFIG_MATRIX: ConfigurationMatrix = {
  [HuskyIvoModel.Auto]: {
    [HuskyFormFactor.Auto]: [HuskyRaidType.JBOD, HuskyRaidType.RAID5, HuskyRaidType.RAID6, HuskyRaidType.RAID10],
    [HuskyFormFactor.Rack1U]: [HuskyRaidType.JBOD],
    [HuskyFormFactor.Rack2U]: [HuskyRaidType.RAID5, HuskyRaidType.RAID6, HuskyRaidType.RAID10],
    [HuskyFormFactor.Desktop]: [HuskyRaidType.JBOD],
    [HuskyFormFactor.Tower]: [HuskyRaidType.JBOD]
  },
  [HuskyIvoModel.HE150D]: {
    [HuskyFormFactor.Auto]: [HuskyRaidType.JBOD],
    [HuskyFormFactor.Desktop]: [HuskyRaidType.Auto, HuskyRaidType.JBOD]
  },
  [HuskyIvoModel.HE350T]: {
    [HuskyFormFactor.Auto]: [HuskyRaidType.JBOD],
    [HuskyFormFactor.Tower]: [HuskyRaidType.Auto, HuskyRaidType.JBOD]
  },
  [HuskyIvoModel.HE350R]: {
    [HuskyFormFactor.Auto]: [HuskyRaidType.JBOD],
    [HuskyFormFactor.Rack1U]: [HuskyRaidType.Auto, HuskyRaidType.JBOD]
  },
  [HuskyIvoModel.HE700R]: {
    [HuskyFormFactor.Auto]: [HuskyRaidType.RAID5, HuskyRaidType.RAID6, HuskyRaidType.RAID10],
    [HuskyFormFactor.Rack1U]: [HuskyRaidType.Auto, HuskyRaidType.RAID5, HuskyRaidType.RAID6, HuskyRaidType.RAID10]
  },
  [HuskyIvoModel.HE1000R]: {
    [HuskyFormFactor.Auto]: [HuskyRaidType.RAID5, HuskyRaidType.RAID6, HuskyRaidType.RAID10],
    [HuskyFormFactor.Rack2U]: [HuskyRaidType.Auto, HuskyRaidType.RAID5, HuskyRaidType.RAID6, HuskyRaidType.RAID10]
  },
  [HuskyIvoModel.HE1800R]: {
    [HuskyFormFactor.Auto]: [HuskyRaidType.RAID5, HuskyRaidType.RAID6, HuskyRaidType.RAID10],
    [HuskyFormFactor.Rack2U]: [HuskyRaidType.Auto, HuskyRaidType.RAID5, HuskyRaidType.RAID6, HuskyRaidType.RAID10]
  }
}

export class HuskyConfigurationService {
  /**
   * Get all valid form factors for a given IVO model
   */
  static getValidFormFactors(ivoModel: HuskyIvoModel): HuskyFormFactor[] {
    const config = HUSKY_CONFIG_MATRIX[ivoModel]
    return config ? (Object.keys(config).map(Number) as HuskyFormFactor[]) : []
  }

  /**
   * Get all valid RAID types for a given IVO model and form factor
   */
  static getValidRaidTypes(ivoModel: HuskyIvoModel, formFactor: HuskyFormFactor): HuskyRaidType[] {
    const config = HUSKY_CONFIG_MATRIX[ivoModel]
    return config?.[formFactor] || []
  }

  /**
   * Get all valid IVO models for a given form factor
   */
  static getValidIvoModels(formFactor: HuskyFormFactor): HuskyIvoModel[] {
    const validModels: HuskyIvoModel[] = []
    for (const [ivoModel, formFactors] of Object.entries(HUSKY_CONFIG_MATRIX)) {
      if (formFactors[formFactor as HuskyFormFactor]) {
        validModels.push(Number(ivoModel) as HuskyIvoModel)
      }
    }
    return validModels
  }

  /**
   * Get all valid IVO models for a given RAID type
   */
  static getValidIvoModelsForRaidType(raidType: HuskyRaidType): HuskyIvoModel[] {
    const validModels: HuskyIvoModel[] = []
    for (const [ivoModel, formFactors] of Object.entries(HUSKY_CONFIG_MATRIX)) {
      for (const raidTypes of Object.values(formFactors)) {
        if (raidTypes.includes(raidType)) {
          validModels.push(Number(ivoModel) as HuskyIvoModel)
          break
        }
      }
    }
    return validModels
  }

  /**
   * Get all valid form factors for a given RAID type
   */
  static getValidFormFactorsForRaidType(raidType: HuskyRaidType, ivoModel?: HuskyIvoModel): HuskyFormFactor[] {
    const validFormFactors: HuskyFormFactor[] = []

    if (ivoModel !== undefined) {
      // Filter by specific IVO model
      const config = HUSKY_CONFIG_MATRIX[ivoModel]
      if (config) {
        for (const [formFactor, raidTypes] of Object.entries(config)) {
          if (raidTypes.includes(raidType)) {
            validFormFactors.push(Number(formFactor) as HuskyFormFactor)
          }
        }
      }
    } else {
      // Check all IVO models
      for (const formFactors of Object.values(HUSKY_CONFIG_MATRIX)) {
        for (const [formFactor, raidTypes] of Object.entries(formFactors)) {
          if (raidTypes.includes(raidType) && !validFormFactors.includes(Number(formFactor) as HuskyFormFactor)) {
            validFormFactors.push(Number(formFactor) as HuskyFormFactor)
          }
        }
      }
    }

    return validFormFactors
  }

  /**
   * Check if a configuration combination is valid
   */
  static isValidConfiguration(ivoModel: HuskyIvoModel, formFactor: HuskyFormFactor, raidType: HuskyRaidType): boolean {
    const validRaidTypes = this.getValidRaidTypes(ivoModel, formFactor)
    return validRaidTypes.includes(raidType)
  }

  /**
   * Get the first valid option from a list, or return Auto if available
   */
  static getDefaultOption<T extends number>(validOptions: T[], currentOption?: T): T {
    if (currentOption !== undefined && validOptions.includes(currentOption)) {
      return currentOption
    }
    return validOptions.includes(0 as T) ? (0 as T) : validOptions[0]
  }
}

// Type definitions for filtering
export type OptionObject = { [key: string]: string }

export interface FilteredOptions {
  ivoModels?: OptionObject
  formFactors?: OptionObject
  raidTypes?: OptionObject
}

// Constants for AutoSelect behavior
const AUTO_SELECT_VALUE = 0

export const isAutoSelectValue = (value: string | number | undefined): boolean => {
  return value !== undefined && Number(value) === AUTO_SELECT_VALUE
}

const filterOptionsByValues = <T extends number>(options: OptionObject, validValues: T[]): OptionObject => {
  return Object.keys(options)
    .filter(key => validValues.includes(Number(key) as T))
    .reduce((acc, key) => {
      acc[key] = options[key]
      return acc
    }, {} as OptionObject)
}

// Unified filtering function that handles AutoSelect logic
const getFilteredOptionsWithAutoSelect = (
  triggerValue: string | number,
  _currentValues: { [key: string]: string | number | undefined },
  availableOptions: { [key: string]: OptionObject },
  filterFunction: Function
): FilteredOptions => {
  // If the trigger value is AutoSelect, preserve all options and current AutoSelect values
  if (isAutoSelectValue(triggerValue)) {
    return availableOptions as FilteredOptions
  }

  // Use the standard filtering logic for non-AutoSelect values
  const filteredOptions = filterFunction()

  // For each filtered result, if the current value is AutoSelect and it exists in the filtered options,
  // keep it available (this is automatically handled since AutoSelect=0 should always be in valid options)
  return filteredOptions
}

// Core filtering functions
export const getOptionListsFromIvoModelChange = (ivoModelValue: string | number, formFactorValue: string | number | undefined, formFactors: OptionObject, raidTypes: OptionObject): FilteredOptions => {
  const ivoModel = Number(ivoModelValue) as HuskyIvoModel
  const formFactor = formFactorValue !== undefined ? (Number(formFactorValue) as HuskyFormFactor) : undefined

  // Get valid form factors and raid types based on IVO model selection
  const validFormFactors = HuskyConfigurationService.getValidFormFactors(ivoModel)
  const filteredFormFactors = filterOptionsByValues(formFactors, validFormFactors)

  let filteredRaidTypes: OptionObject = {}
  if (formFactor !== undefined) {
    const validRaidTypes = HuskyConfigurationService.getValidRaidTypes(ivoModel, formFactor)
    filteredRaidTypes = filterOptionsByValues(raidTypes, validRaidTypes)
  } else {
    // If no form factor selected, get all possible raid types for this IVO model
    const allPossibleRaidTypes = new Set<HuskyRaidType>()
    validFormFactors.forEach(ff => {
      HuskyConfigurationService.getValidRaidTypes(ivoModel, ff).forEach(rt => allPossibleRaidTypes.add(rt))
    })
    filteredRaidTypes = filterOptionsByValues(raidTypes, Array.from(allPossibleRaidTypes))
  }

  return {
    formFactors: filteredFormFactors,
    raidTypes: filteredRaidTypes
  }
}

export const getOptionListsFromFormFactorChange = (formFactorValue: string | number, ivoModelValue: string | number | undefined, ivoModels: OptionObject, raidTypes: OptionObject): FilteredOptions => {
  const formFactor = Number(formFactorValue) as HuskyFormFactor
  const ivoModel = ivoModelValue !== undefined ? (Number(ivoModelValue) as HuskyIvoModel) : undefined

  // Get valid IVO models for this form factor
  const validIvoModels = HuskyConfigurationService.getValidIvoModels(formFactor)
  const filteredIvoModels = filterOptionsByValues(ivoModels, validIvoModels)

  let filteredRaidTypes: OptionObject = {}
  if (ivoModel !== undefined) {
    const validRaidTypes = HuskyConfigurationService.getValidRaidTypes(ivoModel, formFactor)
    filteredRaidTypes = filterOptionsByValues(raidTypes, validRaidTypes)
  } else {
    // If no IVO model selected, get all possible raid types for this form factor
    const allPossibleRaidTypes = new Set<HuskyRaidType>()
    validIvoModels.forEach(im => {
      HuskyConfigurationService.getValidRaidTypes(im, formFactor).forEach(rt => allPossibleRaidTypes.add(rt))
    })
    filteredRaidTypes = filterOptionsByValues(raidTypes, Array.from(allPossibleRaidTypes))
  }

  return {
    ivoModels: filteredIvoModels,
    raidTypes: filteredRaidTypes
  }
}

export const getOptionListsFromRaidTypeChange = (raidTypeValue: string | number, ivoModelValue: string | number | undefined, ivoModels: OptionObject, formFactors: OptionObject): FilteredOptions => {
  const raidType = Number(raidTypeValue) as HuskyRaidType
  const ivoModel = ivoModelValue !== undefined ? (Number(ivoModelValue) as HuskyIvoModel) : undefined

  // Get valid IVO models for this RAID type
  const validIvoModels = HuskyConfigurationService.getValidIvoModelsForRaidType(raidType)
  const filteredIvoModels = filterOptionsByValues(ivoModels, validIvoModels)

  // Get valid form factors for this RAID type
  const validFormFactors = HuskyConfigurationService.getValidFormFactorsForRaidType(raidType, ivoModel)
  const filteredFormFactors = filterOptionsByValues(formFactors, validFormFactors)

  return {
    ivoModels: filteredIvoModels,
    formFactors: filteredFormFactors
  }
}

// Filtering functions that respect AutoSelect values
export const getOptionListsFromRaidTypeChangeWithAutoSelect = (
  raidTypeValue: string | number,
  currentIvoModel: string | number | undefined,
  currentFormFactor: string | number | undefined,
  ivoModels: OptionObject,
  formFactors: OptionObject
): FilteredOptions => {
  return getFilteredOptionsWithAutoSelect(raidTypeValue, { ivoModel: currentIvoModel, formFactor: currentFormFactor }, { ivoModels, formFactors }, () =>
    getOptionListsFromRaidTypeChange(raidTypeValue, currentIvoModel, ivoModels, formFactors)
  )
}

export const getOptionListsFromIvoModelChangeWithAutoSelect = (
  ivoModelValue: string | number,
  currentFormFactor: string | number | undefined,
  currentRaidType: string | number | undefined,
  formFactors: OptionObject,
  raidTypes: OptionObject
): FilteredOptions => {
  return getFilteredOptionsWithAutoSelect(ivoModelValue, { formFactor: currentFormFactor, raidType: currentRaidType }, { formFactors, raidTypes }, () =>
    getOptionListsFromIvoModelChange(ivoModelValue, currentFormFactor, formFactors, raidTypes)
  )
}

export const getOptionListsFromFormFactorChangeWithAutoSelect = (
  formFactorValue: string | number,
  currentIvoModel: string | number | undefined,
  currentRaidType: string | number | undefined,
  ivoModels: OptionObject,
  raidTypes: OptionObject
): FilteredOptions => {
  return getFilteredOptionsWithAutoSelect(formFactorValue, { ivoModel: currentIvoModel, raidType: currentRaidType }, { ivoModels, raidTypes }, () =>
    getOptionListsFromFormFactorChange(formFactorValue, currentIvoModel, ivoModels, raidTypes)
  )
}
